<template>
    <div>
        <h2>Estimate Your Trip Cost</h2>

        <div class="field">
            <label for="fc-distance">Trip distance (km)</label>
            <input id="fc-distance" type="number" min="1" v-model.number="distance" placeholder="e.g. 250">
        </div>

        <div class="field">
            <label for="fc-efficiency">Fuel efficiency (km per liter)</label>
            <input id="fc-efficiency" type="number" min="1" step="0.1" v-model.number="efficiency" placeholder="e.g. 12">
        </div>

        <div class="field">
            <label for="fc-price">Fuel price (₱ per liter)</label>
            <input id="fc-price" type="number" min="1" step="0.01" v-model.number="price" placeholder="e.g. 62.50">
        </div>

        <div class="field">
            <label for="fc-toll">Toll fees (₱, optional)</label>
            <input id="fc-toll" type="number" min="0" v-model.number="toll" placeholder="From the toll calculator">
        </div>

        <div class="field">
            <label>Trip type</label>
            <div class="class-picker">
                <button type="button" :class="{ selected: !roundTrip }" @click="pickTrip(false)">One way</button>
                <button type="button" :class="{ selected: roundTrip }" @click="pickTrip(true)">Round trip</button>
            </div>
        </div>

        <div v-if="valid" class="calc-result">
            <div class="route-label">{{ roundTrip ? 'Round trip' : 'One way' }} · {{ totalDistance }} km · {{ liters }} L</div>
            <div class="headline-rate">₱{{ format(total) }}</div>
            <table class="rate-rows">
                <tbody>
                    <tr><td>Fuel ({{ liters }} L)</td><td>₱{{ format(fuelCost) }}</td></tr>
                    <tr><td>Tolls</td><td>₱{{ format(tollTotal) }}</td></tr>
                </tbody>
            </table>
            <p class="calc-note">Estimates only. Get exact toll fees with the <a href="/" @click="track('result_action', { tool: 'fuel', action: 'to_toll_calculator' })">toll calculator</a>.</p>
        </div>
        <p v-else class="calc-note">Fill in distance, efficiency, and fuel price to see your estimate.</p>
    </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { track, trackOnce, debounced } from '../analytics';

const distance = ref(null);
const efficiency = ref(null);
const price = ref(null);
const toll = ref(null);
const roundTrip = ref(false);

// Prefill from the toll calculator handoff (/tools/fuel-cost-calculator?toll=310&distance=85)
onMounted(() => {
    const params = new URLSearchParams(window.location.search);
    const tollParam = parseFloat(params.get('toll'));
    const distanceParam = parseFloat(params.get('distance'));
    if (tollParam > 0) toll.value = tollParam;
    if (distanceParam > 0) distance.value = distanceParam;

    // The trip table on this page fills the toll in place rather than
    // reloading; the link's href still works when this never runs.
    window.addEventListener('toll:prefill', (e) => {
        const value = Number(e.detail);
        if (value > 0) toll.value = value;
    });
});

function pickTrip(value) {
    roundTrip.value = value;
    track('calc_interaction', { tool: 'fuel', action: 'round_trip', round_trip: value });
}

const valid = computed(() => distance.value > 0 && efficiency.value > 0 && price.value > 0);
const totalDistance = computed(() => (roundTrip.value ? distance.value * 2 : distance.value));
const liters = computed(() => valid.value ? Math.round((totalDistance.value / efficiency.value) * 10) / 10 : 0);
const fuelCost = computed(() => Math.round(liters.value * price.value));
const tollTotal = computed(() => {
    const t = toll.value > 0 ? toll.value : 0;
    return roundTrip.value ? t * 2 : t;
});
const total = computed(() => fuelCost.value + tollTotal.value);

function format(value) {
    return new Intl.NumberFormat('en-PH').format(value);
}

// This calculator has no submit button - it recomputes on every keystroke - so
// the estimate is only reported once the user stops typing. Otherwise a single
// "250" files three events on its way in.
const reportEstimate = debounced(1400);

watch([distance, efficiency, price, toll, roundTrip], () => {
    if (distance.value || efficiency.value || price.value) {
        trackOnce('start:fuel', 'calc_start', { tool: 'fuel' });
    }

    if (!valid.value) return;

    reportEstimate('fuel_calculate', {
        distance_km: distance.value,
        efficiency_kmpl: efficiency.value,
        price_per_liter: price.value,
        liters: liters.value,
        has_toll: toll.value > 0,
        round_trip: roundTrip.value,
        fuel_cost: fuelCost.value,
        total: total.value,
        // Set when the user arrived from the toll calculator's "Add fuel cost".
        handoff: new URLSearchParams(window.location.search).has('toll'),
    });
});
</script>
